// ////////////////////////////////
// ========= Generic dual-list engine (updated) =========

// 1) All sections used in JSX (24 accordions coverage)
export const SECTION_KEYS = [
  "heart",
  "lungs",
  "rightBreast",
  "leftBreast",
  "auscultation",
  "hernia",
  "valva",
  "urethra",
  "vagina",
  "rectalExam",
  "skin",
  "musculoskeletal",
  "neurology",
  "psychiatric",
  "constitutional",
  "integumentary",
  "eyes",
  "enmt",
  "respiratory",
  "cardiovascular",
  "gastrointestinal",
  "gynaecology",
  "endocrine",
  "hematologic",
  "allergic",
  "genitourinary",
  "obstetrics",
  "uterus",
  "parametria",
];

export const SECTION_KEY_SET = new Set(SECTION_KEYS);
export const isValidKey = (key) => SECTION_KEY_SET.has(key);

// 2) Backend field names mapping (ALL notes included)
export const fieldMap = {
  heart: ["heartSelected", "heartUnselected", "heartNotes"],
  lungs: ["lungsSelected", "lungsUnselected", "lungsNotes"],

  rightBreast: ["rightBreastSelected", "rightBreastUnselected", "rightBreastNotes"],
  leftBreast: ["leftBreastSelected", "leftBreastUnselected", "leftBreastNotes"],

  auscultation: ["auscultationSelected", "auscultationUnselected", "auscultationNotes"],
  hernia: ["herniaSelected", "herniaUnselected", "herniaNotes"],

  // NOTE: API me "valva" hi key use ho rahi
  valva: ["valvaSelected", "valvaUnselected", "valvaNotes"],
  urethra: ["urethraSelected", "urethraUnselected", "urethraNotes"],
  vagina: ["vaginaSelected", "vaginaUnselected", "vaginaNotes"],

  rectalExam: ["rectalExamSelected", "rectalExamUnselected", "rectalExamNotes"],
  skin: ["skinSelected", "skinUnselected", "skinNotes"],
  musculoskeletal: ["musculoskeletalSelected", "musculoskeletalUnselected", "musculoskeletalNotes"],

  neurology: ["neurologySelected", "neurologyUnselected", "neurologyNotes"],
  psychiatric: ["psychiatricSelected", "psychiatricUnselected", "psychiatricNotes"],

  constitutional: ["constitutionalSelected", "constitutionalUnselected", "constitutionalNotes"],
  integumentary: ["integumentarySelected", "integumentaryUnselected", "integumentaryNotes"],
  eyes: ["eyesSelected", "eyesUnselected", "eyesNotes"],
  enmt: ["earsNoseMouthThroatSelected", "earsNoseMouthThroatUnselected", "earsNoseMouthThroatNotes"],

  respiratory: ["respiratorySelected", "respiratoryUnselected", "respiratoryNotes"],
  cardiovascular: ["cardiovascularSelected", "cardiovascularUnselected", "cardiovascularNotes"],
  gastrointestinal: ["gastrointestinalSelected", "gastrointestinalUnselected", "gastrointestinalNotes"],

  gynaecology: ["gynaecologySelected", "gynaecologyUnselected", "gynaecologyNotes"],
  endocrine: ["endocrineSelected", "endocrineUnselected", "endocrineNotes"],
  hematologic: ["hematologicSelected", "hematologicUnselected", "hematologicNotes"],

  allergic: ["allergicSelected", "allergicUnselected", "allergicNotes"],

  genitourinary: ["genitourinarySelected", "genitourinaryUnselected", "genitourinaryNotes"],
  obstetrics: ["obstetricsSelected", "obstetricsUnselected", "obstetricsNotes"],

  uterus: ["uterusSelected", "uterusUnselected", "uterusNotes"],
  parametria: ["parametriaSelected", "parametriaUnselected", "parametriaNotes"],
};

// 3) Helpers
const sanitizeKeyword = (s) => String(s || "").replace(/\s+/g, " ").trim();
const dedupe = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const normalizeSection = (section = {}) => {
  const sel = dedupe((section.selected || []).map(sanitizeKeyword));
  let uns = dedupe((section.unselected || []).map(sanitizeKeyword));
  uns = uns.filter((x) => !sel.includes(x)); // overlap remove
  return {
    selected: sel,
    unselected: uns,
    notes: section.notes ?? "",
  };
};

export const makeSection = (overrides = {}) =>
  normalizeSection({
    selected: [],
    unselected: [],
    notes: "",
    ...overrides,
  });

export const initialSections = SECTION_KEYS.reduce((acc, key) => {
  acc[key] = makeSection();
  return acc;
}, {});

// 4) Reducer
export const ACTIONS = {
  ADD: "ADD",
  MOVE: "MOVE",
  SELECT_ALL: "SELECT_ALL",
  CLEAR_ALL: "CLEAR_ALL",
  DELETE: "DELETE",
  REMOVE: "REMOVE",
  SET_NOTES: "SET_NOTES",
  SET_SECTION: "SET_SECTION",
  RESET_SECTION: "RESET_SECTION",
  RESET_ALL: "RESET_ALL",
  BULK_SET: "BULK_SET",
};

export function sectionsReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD: {
      const { key } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      const k = sanitizeKeyword(action.keyword);
      if (!k || s.selected.includes(k) || s.unselected.includes(k)) return state;
      return {
        ...state,
        [key]: normalizeSection({ ...s, unselected: [...s.unselected, k] }),
      };
    }
    case ACTIONS.MOVE: {
      const { key, from, keyword } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      const k = sanitizeKeyword(keyword);
      if (!k) return state;

      if (from === "unselected") {
        if (!s.unselected.includes(k)) return state;
        return {
          ...state,
          [key]: normalizeSection({
            ...s,
            unselected: s.unselected.filter((x) => x !== k),
            selected: [...s.selected, k],
          }),
        };
      } else if (from === "selected") {
        if (!s.selected.includes(k)) return state;
        return {
          ...state,
          [key]: normalizeSection({
            ...s,
            selected: s.selected.filter((x) => x !== k),
            unselected: [...s.unselected, k],
          }),
        };
      }
      return state;
    }
    case ACTIONS.SELECT_ALL: {
      const { key } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      if (!s || s.unselected.length === 0) return state;
      return {
        ...state,
        [key]: normalizeSection({
          ...s,
          selected: [...s.selected, ...s.unselected],
          unselected: [],
        }),
      };
    }
    case ACTIONS.CLEAR_ALL: {
      const { key } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      if (!s || s.selected.length === 0) return state;
      return {
        ...state,
        [key]: normalizeSection({
          ...s,
          unselected: [...s.unselected, ...s.selected],
          selected: [],
        }),
      };
    }
    case ACTIONS.DELETE:
    case ACTIONS.REMOVE: {
      const { key, from, keyword } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      const k = sanitizeKeyword(keyword);
      if (!k || !s[from]) return state;
      const next = { ...s, [from]: s[from].filter((x) => x !== k) };
      return { ...state, [key]: normalizeSection(next) };
    }
    case ACTIONS.SET_NOTES: {
      const { key, notes } = action;
      if (!isValidKey(key)) return state;
      const s = state[key];
      return { ...state, [key]: { ...s, notes: notes ?? "" } };
    }
    case ACTIONS.SET_SECTION: {
      const { key, section } = action;
      if (!isValidKey(key)) return state;
      return { ...state, [key]: normalizeSection(section) };
    }
    case ACTIONS.RESET_SECTION: {
      const { key } = action;
      if (!isValidKey(key)) return state;
      return { ...state, [key]: makeSection() };
    }
    case ACTIONS.RESET_ALL: {
      return { ...initialSections };
    }
    case ACTIONS.BULK_SET: {
      const payload = action.payload || {};
      const next = { ...state };
      Object.keys(payload).forEach((key) => {
        if (isValidKey(key)) next[key] = normalizeSection(payload[key]);
      });
      return next;
    }
    default:
      return state;
  }
}

// 5) API mapping (normalize in/out)
export function toApiPayload(sections) {
  const acc = {};
  Object.entries(fieldMap).forEach(([key, [sel, unsel, notes]]) => {
    const s = normalizeSection(sections[key] || makeSection());
    acc[sel] = s.selected || [];
    acc[unsel] = s.unselected || [];
    if (notes) acc[notes] = s.notes || "";
  });
  return acc;
}

export function fromApiPayload(data) {
  const obj = {};
  Object.entries(fieldMap).forEach(([key, [sel, unsel, notes]]) => {
    const s = {
      selected: Array.isArray(data?.[sel]) ? data[sel] : [],
      unselected: Array.isArray(data?.[unsel]) ? data[unsel] : [],
      notes: notes ? data?.[notes] || "" : "",
    };
    obj[key] = normalizeSection(s);
  });
  return obj;
}

// 6) Dev helper
export function validateFieldMap() {
  const mapKeys = Object.keys(fieldMap);
  const missing = SECTION_KEYS.filter((k) => !mapKeys.includes(k));
  const extra = mapKeys.filter((k) => !SECTION_KEY_SET.has(k));
  return { missing, extra };
}